import { GET_JSON_PERCENTILE_RANK_API } from '@Apis/apiPath';
import { GET_CLUBS_OPTIONS_API } from '@Apis/apiPath';
import { parseDate, isValidDate, getSubMonths } from '@Util/moment.util';

const yyyymmToApiDate = (input) => {
  const s = String(input);

  // 對應 moment 嚴格模式：只接受 6 碼（YYYYMM）或 8 碼（YYYYMMDD）純數字
  if (!/^\d{6}(\d{2})?$/.test(s)) return '';

  // YYYYMM 不在 isValidDate 的嚴格白名單內，先補成 YYYY-MM 再驗證；YYYYMMDD 白名單本來就吃
  const iso = s.length === 6 ? `${s.slice(0, 4)}-${s.slice(4, 6)}` : s;
  if (!isValidDate(iso)) return '';

  return parseDate(iso, 'YYYY-MM-DD');
};

// 取得前 N 個月份（不含當月）
const getPrevMonths = (baseYYYYMM, count = 3) => {
  const cnt = Math.min(Math.max(count, 1), 3);
  const s = String(baseYYYYMM);
  const iso = `${s.slice(0, 4)}-${s.slice(4, 6)}`;
  // 長度檢查對應 moment 嚴格模式「必須剛好 6 碼」的行為
  if (s.length !== 6 || !isValidDate(iso)) return [];
  const months = [];
  for (let i = 1; months.length < cnt; i++) {
    months.push(parseDate(getSubMonths(iso, i), 'YYYYMM'));
  }
  return months;
};

// 正規化 API 
const normalizeArray = (raw) =>
  Array.isArray(raw) ? raw
  : Array.isArray(raw?.data) ? raw.data
  : Array.isArray(raw?.result) ? raw.result
  : [];

const makeApiUrl = (base) => {
  if (typeof base === 'string' && /^https?:\/\//i.test(base)) {
    return new URL(base);
  }
  return new URL(base, window.location.origin);
};

// 載入店鋪資料
const loadClubsData = async () => {
  try {
    const url = makeApiUrl(GET_CLUBS_OPTIONS_API);
    const resp = await fetch(url.toString(), {
      credentials: 'include',
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const json = await resp.json();
    // options API 回傳 { clubOptions, clubStatusOptions }，本頁只用店舖清單
    const clubsData = normalizeArray(json?.result?.clubOptions ?? json?.data?.clubOptions ?? json?.clubOptions);

    return clubsData;
  } catch (err) {
    console.error('[clubs] request failed:', err);
    return [];
  }
};

// 單月請求（rank/total）
const fetchPercentileRank = async ({ date, category = 'rank', download = false, signal = null }) => {
  try {
    const url = makeApiUrl(GET_JSON_PERCENTILE_RANK_API);
    url.searchParams.set('datetime', date);
    url.searchParams.set('category', category);    // 'rank' | 'total'
    if (download) url.searchParams.set('download', 'true');

    const resp = await fetch(url.toString(), {
      credentials: 'include',
      ...(signal && { signal }),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const json = await resp.json();
    return normalizeArray(json);
  } catch (err) {
    console.error('[percentile-rank] request failed:', err);
    return [];
  }
};

// ====== 對外 API ======

export const loadGradesData = async (monthOrDay) => {
  try {
    const date = yyyymmToApiDate(monthOrDay);
    const [gradesData, ageGroupData, clubsData] = await Promise.all([
      fetchPercentileRank({ date, category: 'rank' }),
      fetchPercentileRank({ date, category: 'total' }),
      loadClubsData(),
    ]);
    return { gradesData, ageGroupData, clubsData };
  } catch (error) {
    console.error(`載入 ${monthOrDay} 月份資料失敗:`, error);
    return { gradesData: [], ageGroupData: [], clubsData: [] };
  }
};

export const loadLastThreeMonths = async (baseYYYYMM) => {
  const months = getPrevMonths(baseYYYYMM, 3);       // ['202507','202506','202505']
  const controller = new AbortController();

  try {
    const results = await Promise.all(
      months.map(async (m) => {
        const date = yyyymmToApiDate(m);
        const [gradesData, ageGroupData] = await Promise.all([
          fetchPercentileRank({ date, category: 'rank',  signal: controller.signal }),
          fetchPercentileRank({ date, category: 'total', signal: controller.signal }),
        ]);
        return { month: m, gradesData, ageGroupData };
      })
    );
    return results;
  } catch (e) {
    console.error('[percentile-rank] loadLastThreeMonths failed:', e);
    return months.map((m) => ({ month: m, gradesData: [], ageGroupData: [] }));
  }
};


// ====== 以下為轉換工具 ======

/**
 * 轉換 JSON 資料為圖表格式（rank 專用）
 * @param {Array} jsonData - 從 API 讀取的原始資料
 * @param {string} selectedType - type1~type5
 * @param {Array} selectedAges - 年齡範圍
 * @param {Array} selectedClubs - 店舖ID（字串陣列）
 * @param {Array} clubsData - [{ value, text }]
 * @returns {Object} { name, data }
 */
export const transformJsonDataToChartData = (
  jsonData,
  selectedType,
  selectedAges = [],
  selectedClubs = [],
  clubsData = []
) => {
  if (!Array.isArray(jsonData)) return null;

  const typeMap = { type1: 1, type2: 2, type3: 3, type4: 4, type5: 5 };
  const targetType = typeMap[selectedType];
  if (!targetType) return null;

  let filteredData = jsonData.filter((item) => item.LeaderboardType === targetType);
  if (filteredData.length === 0) return null;

  if (selectedAges.length > 0) {
    filteredData = filteredData.filter((item) =>
      selectedAges.some((ageRange) => {
        const [minAge, maxAge] = parseAgeRange(ageRange);
        return item.Age >= minAge && item.Age <= maxAge;
      })
    );
  }

  if (selectedClubs.length > 0) {
    filteredData = filteredData.filter((item) => selectedClubs.includes(String(item.ClubID)));
  }

  const getAgeRange = (age) => {
    if (age <= 25) return '0-25';
    if (age <= 30) return '26-30';
    if (age <= 35) return '31-35';
    if (age <= 40) return '36-40';
    if (age <= 45) return '41-45';
    if (age <= 50) return '46-50';
    if (age <= 55) return '51-55';
    if (age <= 60) return '56-60';
    if (age <= 65) return '61-65';
    if (age <= 70) return '66-70';
    return '70+';
  };

  const clubNameMap = {};
  if (Array.isArray(clubsData)) {
    clubsData.forEach((club) => { clubNameMap[club.value] = club.text; });
  }

  const scatterData = filteredData.map((item) => {
    const itemClubIDStr = String(item.ClubID);
    const clubName = clubNameMap[itemClubIDStr] || `店鋪${item.ClubID}`;
    
    return {
      value: [item.DiffValue, item.ClubsAllRanking],
      rank: item.ClubsAllRanking,
      originalValue: item.DiffValue,
      age: item.Age,
      ageRange: getAgeRange(item.Age),
      clubID: item.ClubID,
      clubName: clubName,
    };
  });

  return { name: getTypeName(selectedType), data: scatterData };
};


const parseAgeRange = (ageRange) => {
  if (ageRange === '70+') return [70, 120];
  const [min, max] = ageRange.split('-').map(Number);
  return [min, max];
};

export const getTypeName = (selectedType) => {
  const typeNameMap = {
    type1: '體重(KG)',
    type2: '體脂肪重(KG)',
    type3: '體脂肪率(%)',
    type4: '骨骼肌重(KG)',
    type5: '總體圍(CM)',
  };
  return typeNameMap[selectedType] || '';
};

export const ageRangeMap = [
  { key: '0-25', min: 0, max: 25, label: '0-25歲', color: '#FF6B6B' },
  { key: '26-30', min: 26, max: 30, label: '26-30歲', color: '#4ECDC4' },
  { key: '31-35', min: 31, max: 35, label: '31-35歲', color: '#45B7D1' },
  { key: '36-40', min: 36, max: 40, label: '36-40歲', color: '#96CEB4' },
  { key: '41-45', min: 41, max: 45, label: '41-45歲', color: '#FFEAA7' },
  { key: '46-50', min: 46, max: 50, label: '46-50歲', color: '#DDA0DD' },
  { key: '51-55', min: 51, max: 55, label: '51-55歲', color: '#98D8C8' },
  { key: '56-60', min: 56, max: 60, label: '56-60歲', color: '#F7DC6F' },
  { key: '61-65', min: 61, max: 65, label: '61-65歲', color: '#BB8FCE' },
  { key: '66-70', min: 66, max: 70, label: '66-70歲', color: '#85C1E9' },
  { key: '70+',   min: 71, max: 120, label: '70歲以上', color: '#F8C471' },
];

export const getAgeRangeKey = (age) => {
  const matched = ageRangeMap.find((range) => age >= range.min && age <= range.max);
  return matched?.key || '未知';
};

export const getAgeRangeDisplayName = (key) => {
  const matched = ageRangeMap.find((range) => range.key === key);
  return matched || { label: key, color: '#90EE90' };
};
